import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnDestroy, ViewChild } from '@angular/core';

export type PhotoTipsKind = 'space' | 'logo' | 'banner' | 'share';

interface PhotoTipsContent {
  title: string;
  tips: string[];
}

/**
 * Botón compacto «Consejos» junto a cada punto donde el dueño sube fotos. Al
 * tocarlo abre una burbuja con cómo subirlas para que se vean bien en el
 * sitio público (cada lugar recorta distinto: tarjetas 3:2, banner
 * panorámico, logo circular, vista de enlace 1200x630). La burbuja es fija
 * a la ventana para que ningún contenedor con scroll la recorte.
 */
@Component({
  selector: 'app-photo-tips',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button #trigger type="button" class="tips-trigger" [class.active]="isOpen"
      [attr.aria-expanded]="isOpen" [attr.aria-controls]="popId" (click)="toggle($event)">
      <i class="bi bi-lightbulb-fill" aria-hidden="true"></i><span>{{ label }}</span><i class="bi bi-chevron-down chevron" aria-hidden="true"></i>
    </button>
    <div *ngIf="isOpen" class="tips-pop" [id]="popId" role="dialog" [attr.aria-label]="content.title"
      [style.top.px]="top" [style.left.px]="left" [style.width.px]="width" (click)="$event.stopPropagation()">
      <div class="tips-head">
        <strong><i class="bi bi-lightbulb" aria-hidden="true"></i> {{ content.title }}</strong>
        <button type="button" class="tips-close" aria-label="Cerrar consejos" (click)="close()"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
      </div>
      <ul>
        <li *ngFor="let tip of content.tips"><i class="bi bi-check2" aria-hidden="true"></i><span [innerHTML]="tip"></span></li>
      </ul>
    </div>
  `,
  styles: [`
    :host { display: inline-flex; vertical-align: middle; }
    /* Píldora ámbar con chevron: se lee como botón y llama la atención sin
       ocupar espacio. */
    .tips-trigger { display: inline-flex; align-items: center; gap: .32rem; min-height: 0; padding: .26rem .62rem; border: 1px solid rgba(217, 152, 18, .45); border-radius: 999px; color: #8a5a00; background: rgba(245, 185, 66, .16); font: inherit; font-size: .75rem; font-weight: 700; line-height: 1.3; letter-spacing: normal; text-transform: none; cursor: pointer; white-space: nowrap; }
    .tips-trigger .bi-lightbulb-fill { color: #e0a21b; font-size: .82rem; }
    .tips-trigger .chevron { font-size: .62rem; transition: transform .18s ease; }
    .tips-trigger.active .chevron { transform: rotate(180deg); }
    .tips-trigger:hover, .tips-trigger.active { border-color: rgba(217, 152, 18, .75); background: rgba(245, 185, 66, .28); }
    :host-context(.tusala-dark-theme) .tips-trigger { color: #f7c95c; background: rgba(245, 185, 66, .12); }
    .tips-trigger:focus-visible { outline: 2px solid var(--ts-text-blue-250-3, #2563eb); outline-offset: 2px; }
    .tips-pop { position: fixed; z-index: 5000; box-sizing: border-box; max-height: min(70vh, 460px); overflow: auto; padding: .7rem .85rem .8rem; border: 1px solid var(--ts-border-strong, rgba(120,150,200,.35)); border-radius: 12px; color: var(--ts-text-navy-800-3, #1e293b); background: var(--ts-surface, #fff); box-shadow: 0 14px 38px rgba(2, 8, 23, .22); font-size: .78rem; font-weight: 400; line-height: 1.45; text-align: left; letter-spacing: normal; text-transform: none; white-space: normal; animation: tipsIn .14s ease-out; }
    .tips-head { display: flex; align-items: flex-start; justify-content: space-between; gap: .5rem; margin-bottom: .45rem; }
    .tips-head strong { font-size: .8rem; }
    .tips-head strong .bi { color: #e0a21b; }
    .tips-close { flex: none; width: 24px; height: 24px; min-height: 0; display: grid; place-items: center; padding: 0; border: 0; border-radius: 50%; color: var(--ts-text-muted, #64748b); background: transparent; cursor: pointer; font-size: .75rem; }
    .tips-close:hover { background: var(--ts-bg-blue-400-a12, rgba(37,99,235,.08)); }
    ul { margin: 0; padding: 0 0 0 1.15rem; list-style: none; display: grid; gap: .32rem; }
    li { position: relative; }
    li > .bi { position: absolute; left: -1.1rem; top: .05rem; color: #22a06b; }
    @keyframes tipsIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
    @media (prefers-reduced-motion: reduce) { .tips-pop { animation: none; } }
  `],
})
export class PhotoTipsComponent implements OnDestroy {
  private static seq = 0;

  @Input() kind: PhotoTipsKind = 'space';
  /** Texto del botón. */
  @Input() label = 'Ver consejos';
  @ViewChild('trigger', { static: true }) trigger!: ElementRef<HTMLButtonElement>;

  readonly popId = `photo-tips-${++PhotoTipsComponent.seq}`;
  isOpen = false;
  top = 0;
  left = 0;
  width = 270;

  // Breve a propósito: 3-4 puntos que se leen de un vistazo.
  private static readonly CONTENT: Record<PhotoTipsKind, PhotoTipsContent> = {
    space: {
      title: 'Fotos que se ven completas',
      tips: [
        'Tómala en <strong>horizontal</strong>.',
        'Lo importante, <strong>al centro</strong>.',
        'Con <strong>buena luz</strong> y sin texto ni capturas.',
        'La primera foto es la <strong>portada</strong>.',
      ],
    },
    logo: {
      title: 'Logo que se ve bien',
      tips: [
        'Imagen <strong>cuadrada</strong> (mín. 400 px).',
        'Se ve en un <strong>círculo</strong>: deja margen.',
        'Ideal PNG con <strong>fondo transparente</strong>.',
      ],
    },
    banner: {
      title: 'Portada que se ve bien',
      tips: [
        '<strong>Horizontal y amplia</strong> (ideal 1920 × 800).',
        'Lo importante, <strong>al centro</strong>.',
        '<strong>Sin texto</strong>: encima va tu título.',
      ],
    },
    share: {
      title: 'Foto para compartir',
      tips: [
        'Usa una foto <strong>horizontal</strong>.',
        'Lo importante, <strong>al centro</strong>.',
        'Se ajusta sola a 1200 × 630.',
      ],
    },
  };

  // Fase de captura: también detecta el scroll de contenedores internos
  // (p. ej. el cuerpo del modal «Nuevo espacio»), no solo el de la ventana.
  private readonly onAnyScroll = (event: Event): void => {
    if (this.isOpen && !this.host.nativeElement.contains(event.target as Node)) this.close();
  };

  constructor(private readonly host: ElementRef<HTMLElement>) {
    document.addEventListener('scroll', this.onAnyScroll, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onAnyScroll, true);
  }

  get content(): PhotoTipsContent {
    return PhotoTipsComponent.CONTENT[this.kind] ?? PhotoTipsComponent.CONTENT.space;
  }

  toggle(event: Event): void {
    // Puede estar dentro de un <label> o un <form>: no debe activarlos.
    event.preventDefault();
    event.stopPropagation();
    if (this.isOpen) { this.close(); return; }
    this.position();
    this.isOpen = true;
  }

  close(): void {
    this.isOpen = false;
  }

  /** Bajo el botón; si no cabe abajo, encima. Siempre dentro de la ventana. */
  private position(): void {
    const rect = this.trigger.nativeElement.getBoundingClientRect();
    const margin = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    this.width = Math.min(270, vw - margin * 2);
    this.left = Math.max(margin, Math.min(rect.left, vw - this.width - margin));
    const estimatedHeight = Math.min(vh * 0.7, 460, 60 + this.content.tips.length * 26);
    const below = rect.bottom + 8;
    this.top = below + estimatedHeight > vh - margin && rect.top - estimatedHeight - 8 > margin
      ? rect.top - estimatedHeight - 8
      : below;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isOpen && !this.host.nativeElement.contains(event.target as Node)) this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.isOpen) return;
    this.close();
    this.trigger.nativeElement.focus();
  }

  // Es fija a la ventana: si cambia de tamaño, se cierra en vez de quedar
  // flotando lejos del botón (el scroll lo cubre onAnyScroll).
  @HostListener('window:resize')
  onViewportChange(): void {
    if (this.isOpen) this.close();
  }
}
